import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import {
  confirmShifts,
  crewQuery,
  dressCodesQuery,
  MISSING_TO_CONFIRM,
  placeLabel,
  type Shift,
  toConfirmQuery,
  updateShift,
} from '../api/crew.ts';
import { stockLocationsQuery } from '../api/inventory.ts';
import { venuesQuery } from '../api/venues.ts';
import { Button } from '../components/button.tsx';

// CB-REQ CRW-16 at v1.978 – v1.996 (crew parts 3a and 4A): accepted shifts the office should have
// confirmed by now — inside the company's window before they start. Confirming needs a place, a
// dress code and a contact; a shift lacking any lists them, fillable in place, before Confirm.

const when = (local: string) => `${local.slice(0, 10)} ${local.slice(11, 16)}`;

/** For the home screen: each shift to confirm, with its job, and a way to confirm them. */
export function ShiftsToConfirm({ mayConfirm }: { mayConfirm: boolean }) {
  const due = useQuery(toConfirmQuery);
  const queryClient = useQueryClient();
  const confirm = useMutation({
    mutationFn: (ids: string[]) => confirmShifts(ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shifts'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });
  if (!due.data || due.data.length === 0) return null;
  const ready = due.data.filter((s) => s.missingToConfirm.length === 0);
  return (
    <section aria-labelledby="to-confirm" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="to-confirm" className="section-title">
          Shifts to confirm
        </h2>
        {mayConfirm && ready.length > 1 ? (
          <Button
            variant="quiet"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate(ready.map((s) => s.id))}
          >
            Confirm all ({ready.length})
          </Button>
        ) : null}
      </div>
      {confirm.error ? (
        <p role="alert" className="alert">
          {confirm.error.message}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2 text-sm">
        {due.data.map((s: Shift) => (
          <li key={s.id} className="flex flex-wrap items-baseline gap-2">
            <span className="font-semibold">{s.crewMember?.name ?? 'Nobody yet'}</span>
            {s.role ? <span>{s.role.name}</span> : null}
            <span className="text-ink-soft">{when(s.window.localStart)}</span>
            <span className="text-ink-soft">{placeLabel(s)}</span>
            {s.bookedForJob ? (
              <Link to="/jobs/$jobId" params={{ jobId: s.bookedForJob.id }} className="link">
                J-{s.bookedForJob.number} {s.bookedForJob.name}
              </Link>
            ) : null}
            {s.missingToConfirm.length > 0 ? (
              <span className="tag tag-accent-2">
                Needs {s.missingToConfirm.map((m) => MISSING_TO_CONFIRM[m]).join(', ')}
              </span>
            ) : null}
            {mayConfirm && s.missingToConfirm.length > 0 ? <FillMissing shift={s} /> : null}
            {mayConfirm && s.missingToConfirm.length === 0 ? (
              <Button
                variant="quiet"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate([s.id])}
              >
                Confirm<span className="sr-only"> {s.crewMember?.name}’s shift</span>
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** CRW-16 at v1.996: what a shift lacks to be confirmed, given in place; Confirm then follows. */
function FillMissing({ shift }: { shift: Shift }) {
  const missing = new Set(shift.missingToConfirm);
  const venues = useQuery({ ...venuesQuery, enabled: missing.has('place') });
  const locations = useQuery({ ...stockLocationsQuery, enabled: missing.has('place') });
  const dressCodes = useQuery({ ...dressCodesQuery, enabled: missing.has('dress_code') });
  const crew = useQuery({ ...crewQuery(false), enabled: missing.has('contact') });
  const queryClient = useQueryClient();
  const [place, setPlace] = useState('');
  const [dressCodeId, setDressCodeId] = useState('');
  const [contactId, setContactId] = useState('');
  const depot = (locations.data ?? []).find((l) => l.kind === 'depot');
  const set = useMutation({
    mutationFn: () => {
      const [kind, id] = place.split(':');
      return updateShift(shift.id, {
        ...(place ? { venueId: kind === 'venue' ? id : null } : {}),
        ...(place ? { locationId: kind === 'location' ? id : null } : {}),
        ...(dressCodeId ? { dressCodeId } : {}),
        ...(contactId ? { contactCrewMemberId: contactId } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shifts'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });
  // Named by person and day, so each row's pickers are told apart (WCAG 2.2, INV-13).
  const who = `${shift.crewMember?.name ?? 'the open shift'}, ${when(shift.window.localStart)}`;
  return (
    <span className="flex flex-wrap items-center gap-2">
      {missing.has('place') ? (
        <select
          aria-label={`Where for ${who}`}
          className="input"
          value={place}
          onChange={(e) => setPlace(e.currentTarget.value)}
        >
          <option value="">Where…</option>
          {(venues.data ?? []).map((v) => (
            <option key={v.id} value={`venue:${v.id}`}>
              {v.name}
            </option>
          ))}
          {depot ? <option value={`location:${depot.id}`}>{depot.name}</option> : null}
        </select>
      ) : null}
      {missing.has('dress_code') ? (
        <select
          aria-label={`Dress code for ${who}`}
          className="input"
          value={dressCodeId}
          onChange={(e) => setDressCodeId(e.currentTarget.value)}
        >
          <option value="">Dress code…</option>
          {(dressCodes.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      ) : null}
      {missing.has('contact') ? (
        <select
          aria-label={`Contact for ${who}`}
          className="input"
          value={contactId}
          onChange={(e) => setContactId(e.currentTarget.value)}
        >
          <option value="">Contact…</option>
          {(crew.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.displayName}
            </option>
          ))}
        </select>
      ) : null}
      <Button
        variant="quiet"
        disabled={(!place && !dressCodeId && !contactId) || set.isPending}
        onClick={() => set.mutate()}
      >
        Set
      </Button>
      {set.error ? (
        <span role="alert" className="text-accent-2-700">
          {set.error.message}
        </span>
      ) : null}
    </span>
  );
}
